import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, AlertCircle, LayoutGrid, RefreshCw } from 'lucide-react';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/contexts/AuthContext';
import { SignOutButton } from '@/components/auth/SignOutButton';
import {
  adsApi,
  brandsApi,
  flipkartAdsApi,
  isAmazonReauthRequiredError,
  type BrandRecord,
  type FlipkartAdsAdAccount,
} from '@/api/client';
import {
  AmazonOauthBanner,
  ConnectAmazonButton,
} from '@/components/AmazonOauthConnections';
import {
  AccountAdminSyncButtons,
  AccountAdminSyncMasterPanel,
} from '@/components/accounts/AccountAdminSyncControls';
import {
  accountLabel,
  extractAccountRows,
  rowStableKey,
  uniqueAccountRows,
  type LinkedAccountRow,
} from '@/api/managerAccounts';
import { describeFlipkartSyncStatus, describeSyncStatus } from '@/api/syncStatus';
import { FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY } from '@/components/FlipkartAdsOauthConnections';
import { flipkartAdAccountLabel, workspaceCampaignPath } from '@/api/workspaceScope';
import { AdvertisingAccountRegionGroups } from '@/components/accounts/AdvertisingAccountRegionGroups';
import {
  AnalysisActionBar,
  ConnectAccountsCta,
  ConnectedPlatformSection,
  EmptyAccountState,
  PlatformAccountSection,
} from '@/components/accounts/advertisingAccountSections';
import { amazonAccountRegion, flipkartAccountRegion } from '@/api/advertisingAccountRegions';

function AccountSyncActions({
  profileId,
  accountName,
  managerAccountName,
  showAdminReportSync,
  adminSyncDisabled,
}: {
  profileId: string;
  accountName?: string;
  managerAccountName?: string;
  showAdminReportSync: boolean;
  adminSyncDisabled?: boolean;
}) {
  const qc = useQueryClient();

  const { data: syncStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['profile-sync-status', profileId],
    queryFn: async () => {
      const res = await adsApi.getProfileSyncStatus(profileId);
      if (!res.success) throw new Error('Failed to load sync status');
      return res.data;
    },
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? 15_000 : false,
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await adsApi.triggerProfileCatalogSync(profileId);
      if (!res.success) throw new Error('Sync failed to start');
      return res.data;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['profile-sync-status', profileId] });
      qc.setQueryData(['profile-sync-status', profileId], (prev: typeof syncStatus) =>
        prev ? { ...prev, status: 'running', lastError: null } : prev
      );
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['profile-sync-status', profileId] });
    },
    onError: async () => {
      await qc.invalidateQueries({ queryKey: ['profile-sync-status', profileId] });
    },
  });

  const reportsSyncMutation = useMutation({
    mutationFn: async () => {
      const res = await adsApi.startProfileUnifiedReportSync(profileId, {
        accountName,
        managerAccountName,
      });
      if (!res.success) throw new Error('Report sync failed to start');
      return res.data;
    },
  });

  const isSyncing = syncMutation.isPending || syncStatus?.status === 'running';
  const statusText = statusLoading ? 'Loading sync…' : describeSyncStatus(syncStatus);
  const errorMessage = syncMutation.isError
    ? (syncMutation.error as Error).message
    : reportsSyncMutation.isError
      ? (reportsSyncMutation.error as Error).message
      : syncStatus?.lastError;

  return (
    <div className="flex w-full shrink-0 flex-col items-start gap-1 sm:w-auto sm:items-end">
      {statusText ? (
        <span
          className={`text-xs max-w-[11rem] text-right truncate ${
            syncStatus?.status === 'failed' || syncMutation.isError
              ? 'text-warning'
              : 'text-foreground-subtle'
          }`}
          title={errorMessage ?? statusText}
        >
          {statusText}
        </span>
      ) : null}
      <div className="flex flex-wrap items-center justify-start gap-1.5 sm:justify-end">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={isSyncing}
          onClick={() => syncMutation.mutate()}
          className="whitespace-nowrap"
        >
          {isSyncing ? (
            <>
              <Spinner className="h-3.5 w-3.5 mr-1.5" />
              Syncing…
            </>
          ) : (
            <>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              Sync now
            </>
          )}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={reportsSyncMutation.isPending}
          onClick={() => reportsSyncMutation.mutate()}
          className="whitespace-nowrap"
          title="Gap-fill last 12 complete calendar months from S3; skip months already stored; Amazon create/retrieve runs one month at a time."
        >
          {reportsSyncMutation.isPending ? (
            <>
              <Spinner className="h-3.5 w-3.5 mr-1.5" />
              Syncing reports…
            </>
          ) : (
            <>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              Sync reports
            </>
          )}
        </Button>
        {showAdminReportSync ? (
          <AccountAdminSyncButtons
            profileId={profileId}
            meta={{ accountName, managerAccountName }}
            disabled={adminSyncDisabled}
          />
        ) : null}
      </div>
    </div>
  );
}

function AccountListRow({
  row,
  showAdminReportSync,
  adminSyncDisabled,
}: {
  row: LinkedAccountRow;
  showAdminReportSync: boolean;
  adminSyncDisabled?: boolean;
}) {
  const profileId = row.profileId ?? null;
  const label = accountLabel(row);

  const accountDetails = (
    <div className="min-w-0 flex-1">
      <div className="truncate text-body-md font-semibold text-brand-ink">{label}</div>
      <div className="mt-1 flex flex-wrap gap-x-1.5 text-xs text-foreground-muted">
        {row.accountType && <span>{row.accountType}</span>}
        {row.marketplaceId && <span className="font-mono">· {row.marketplaceId}</span>}
      </div>
      {profileId && (
        <div className="mt-0.5 break-all font-mono text-xs text-foreground-subtle">
          Profile: {profileId}
        </div>
      )}
      {!profileId && (
        <div className="mt-0.5 text-xs text-warning">
          No profile id in payload — cannot open campaigns
        </div>
      )}
    </div>
  );

  return profileId ? (
        <div className="flex w-full flex-col gap-3 rounded-2xl border border-border bg-surface px-4 py-3.5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5">
          <Link
            to={`/accounts/${encodeURIComponent(profileId)}/campaigns/dashboard`}
            state={{ accountName: label }}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
          >
            {accountDetails}
            <ChevronRight className="h-5 w-5 shrink-0 text-foreground-subtle" aria-hidden />
          </Link>
          <AccountSyncActions
            profileId={profileId}
            accountName={label}
            managerAccountName={row.managerAccountName ?? undefined}
            showAdminReportSync={showAdminReportSync}
            adminSyncDisabled={adminSyncDisabled}
          />
        </div>
      ) : (
        <div className="flex w-full cursor-not-allowed items-center justify-between rounded-2xl border border-border bg-surface px-4 py-3.5 text-left opacity-50 shadow-sm sm:px-5">
          {accountDetails}
          <ChevronRight className="w-5 h-5 text-chrome-label shrink-0" />
        </div>
      );
}

function BrandAccountListRow({ brand }: { brand: BrandRecord }) {
  const to = workspaceCampaignPath({ platform: 'brand', accountId: brand.id }, 'dashboard');
  return (
    <div className="flex w-full items-center rounded-2xl border border-border bg-surface px-4 py-3.5 shadow-sm sm:px-5">
      <Link
        to={to}
        state={{ accountName: brand.name }}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="truncate text-body-md font-semibold text-brand-ink">{brand.name}</div>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-foreground-subtle" aria-hidden />
      </Link>
    </div>
  );
}

function FlipkartAccountListRow({ account }: { account: FlipkartAdsAdAccount }) {
  const label = flipkartAdAccountLabel(account);
  const syncLabel = describeFlipkartSyncStatus(account.synced_at);
  const to = workspaceCampaignPath(
    {
      platform: 'flipkart',
      credentialId: account.credential_id,
      accountId: account.ad_account_id,
    },
    'dashboard'
  );

  return (
      <div className="flex w-full flex-col gap-3 rounded-2xl border border-border bg-surface px-4 py-3.5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5">
        <Link
          to={to}
          state={{ accountName: label }}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <div className="min-w-0 flex-1">
            <div className="truncate text-body-md font-semibold text-brand-ink">{label}</div>
            <div className="mt-1 flex flex-wrap gap-x-1.5 break-all text-xs text-foreground-muted">
              <span>Flipkart Ads</span>
              {account.status ? <span>· {account.status}</span> : null}
              <span className="font-mono">{account.ad_account_id}</span>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-foreground-subtle" aria-hidden />
        </Link>
        <span
          className="max-w-[11rem] shrink-0 truncate text-xs text-foreground-subtle sm:text-right"
          title={syncLabel}
        >
          {syncLabel}
        </span>
      </div>
  );
}

export function AccountsPage() {
  const { user, hasPermission, isSuperAdmin } = useAuth();
  const showAdmin = hasPermission('admin.users.read');
  const isDemo = Boolean(user?.is_demo);
  const [searchParams] = useSearchParams();
  const [adminBulkPending, setAdminBulkPending] = useState(false);
  const onAdminBulkPendingChange = useCallback((pending: boolean) => {
    setAdminBulkPending(pending);
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['manager-accounts', user?.id],
    queryFn: async () => {
      const res = await adsApi.getManagerAccounts();
      if (!res.success) throw new Error('Failed to load accounts');
      return res.data;
    },
    enabled: Boolean(user?.id),
  });

  const flipkartQuery = useQuery({
    queryKey: FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY,
    queryFn: async () => {
      const res = await flipkartAdsApi.listAllAccounts();
      if (!res.success) throw new Error('Failed to load Flipkart accounts');
      return res.data.accounts;
    },
    enabled: Boolean(user?.id) && !isDemo,
  });

  const brandsQuery = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const res = await brandsApi.list();
      if (!res.success || !res.data) throw new Error('Failed to load brands');
      return res.data.brands;
    },
    enabled: Boolean(user?.id) && !isDemo,
  });

  useEffect(() => {
    // OAuth now lands on /connections; keep a soft redirect if an old link hits /accounts.
    const amazon = searchParams.get('amazon');
    if (!amazon) return;
    const next = new URLSearchParams(searchParams);
    const qs = next.toString();
    window.location.replace(`/connections${qs ? `?${qs}` : ''}`);
  }, [searchParams]);

  const rows = uniqueAccountRows(extractAccountRows(data));
  const flipkartAccounts = flipkartQuery.data ?? [];
  const brands = brandsQuery.data ?? [];
  const amazonEmpty = rows.length === 0;
  const flipkartSettledEmpty =
    !flipkartQuery.isLoading && !flipkartQuery.isError && flipkartAccounts.length === 0;
  const hasAnyAccount = rows.length > 0 || flipkartAccounts.length > 0 || brands.length > 0;
  const syncableRows = useMemo(
    () => rows.filter((row): row is LinkedAccountRow & { profileId: string } => Boolean(row.profileId)),
    [rows]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <Spinner className="w-10 h-10" />
      </div>
    );
  }

  if (error) {
    const reauth = isAmazonReauthRequiredError(error);
    return (
      <div className="min-h-screen bg-page p-8">
        <div className="max-w-lg mx-auto bg-surface rounded-xl border border-error/20 p-6 text-center">
          <AlertCircle className="w-10 h-10 text-error mx-auto mb-3" />
          <p className="text-foreground font-medium mb-2">
            {reauth ? 'Amazon reconnection required' : 'Could not load manager accounts'}
          </p>
          <p className="text-sm text-foreground-muted mb-4">
            {reauth
              ? 'Your Amazon Ads refresh token is invalid or expired. Connect Amazon again to restore access.'
              : (error as Error).message}
          </p>
          {reauth && !isDemo ? (
            <ConnectAmazonButton />
          ) : (
            <Button type="button" onClick={() => refetch()}>
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page">
      <header className="px-3 pt-3 sm:px-4 sm:pt-4">
        <div className="mx-auto flex max-w-5xl flex-nowrap items-center justify-between gap-3 rounded-2xl border border-border bg-surface px-3 py-2.5 shadow-sm sm:px-4 sm:py-3">
          <ProductBrand variant="light" showTagline={false} className="min-w-0" />
          <div className="flex flex-nowrap items-center justify-end gap-2 sm:gap-3">
            {!isDemo && <ConnectAmazonButton toolbar />}
            {!isDemo ? <span className="h-8 w-px shrink-0 bg-border" aria-hidden /> : null}
            {showAdmin && (
              <>
                <Link
                  to="/admin/account-metrics"
                  className="text-body-sm font-medium text-brand hover:underline"
                >
                  Account metrics
                </Link>
                <Link to="/admin/users" className="text-body-sm font-medium text-brand hover:underline">
                  Admin
                </Link>
                {isSuperAdmin ? (
                  <>
                    <Link
                      to="/admin/report-coverage"
                      className="text-body-sm font-medium text-brand hover:underline"
                    >
                      Report coverage
                    </Link>
                    <Link
                      to="/admin/stream-subscriptions"
                      className="text-body-sm font-medium text-brand hover:underline"
                    >
                      Stream subscriptions
                    </Link>
                    <Link
                      to="/admin/settings"
                      className="text-body-sm font-medium text-brand hover:underline"
                    >
                      SP-API
                    </Link>
                  </>
                ) : null}
              </>
            )}
            <Link
              to="/brands"
              className="text-body-sm font-medium text-brand hover:underline"
            >
              Brands
            </Link>
            <Link
              to="/dashboard"
              className="inline-flex h-[34px] items-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-body-sm font-medium text-foreground shadow-sm hover:border-brand"
            >
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
              Dashboard
            </Link>
            <SignOutButton withIcon />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-3 px-3 py-4 sm:px-4">
        {!isDemo && <AmazonOauthBanner />}

        <div>
          <h1 className="font-display text-headline-lg font-semibold text-brand-ink sm:text-headline-xl">
            Manage your advertising accounts
          </h1>
          <p className="mt-1 max-w-2xl text-body-md text-foreground-muted">
            Enabled workspaces ready for campaign management. Set up or toggle accounts from
            Connections.
          </p>
          {isDemo ? (
            <p className="mt-2 text-body-sm text-foreground-subtle">
              You are viewing the isolated demo workspace. Live Amazon accounts are not available here.
            </p>
          ) : null}
        </div>

        {isSuperAdmin ? (
          <AccountAdminSyncMasterPanel
            rows={syncableRows}
            onPendingChange={onAdminBulkPendingChange}
          />
        ) : null}

        {amazonEmpty ? (
          <PlatformAccountSection
            platform="amazon"
            title="Amazon Ads"
            description="Connected Amazon advertising accounts"
          >
            <EmptyAccountState
              title={isDemo ? 'No demo accounts are available.' : 'No enabled Amazon accounts yet.'}
              detail={
                isDemo
                  ? 'Contact support if this persists.'
                  : 'Connect Amazon and complete setup on the Connections page.'
              }
            />
          </PlatformAccountSection>
        ) : (
          <ConnectedPlatformSection title="Amazon Ads">
            <AdvertisingAccountRegionGroups
              items={rows}
              resolveRegion={amazonAccountRegion}
              getItemKey={(row, index) => rowStableKey(row, index)}
              renderItem={(row) => (
                <AccountListRow
                  row={row}
                  showAdminReportSync={isSuperAdmin}
                  adminSyncDisabled={adminBulkPending}
                />
              )}
            />
          </ConnectedPlatformSection>
        )}

        {!isDemo ? (
          flipkartQuery.isLoading ? (
            <ConnectedPlatformSection title="Flipkart Ads">
              <div className="flex justify-center rounded-2xl border border-border bg-surface py-6 shadow-sm">
                <Spinner className="h-6 w-6" />
              </div>
            </ConnectedPlatformSection>
          ) : flipkartQuery.isError ? (
            <ConnectedPlatformSection title="Flipkart Ads">
              <div className="rounded-2xl border border-border bg-surface px-4 py-5 text-center text-foreground-muted shadow-sm">
                <p className="text-body-md">Could not load Flipkart ad accounts.</p>
                <Button type="button" className="mt-3" onClick={() => void flipkartQuery.refetch()}>
                  Retry
                </Button>
              </div>
            </ConnectedPlatformSection>
          ) : flipkartSettledEmpty ? (
            <PlatformAccountSection
              platform="flipkart"
              title="Flipkart Ads"
              description="Connected Flipkart advertising accounts"
            >
              <EmptyAccountState
                title="No Flipkart ad accounts yet."
                detail="Connect Flipkart Ads and sync accounts on the Connections page."
              />
            </PlatformAccountSection>
          ) : (
            <ConnectedPlatformSection title="Flipkart Ads">
              <AdvertisingAccountRegionGroups
                items={flipkartAccounts}
                resolveRegion={flipkartAccountRegion}
                getItemKey={(account) => account.id}
                renderItem={(account) => <FlipkartAccountListRow account={account} />}
              />
            </ConnectedPlatformSection>
          )
        ) : null}

        {!isDemo ? (
          brandsQuery.isLoading ? (
            <ConnectedPlatformSection title="Brands">
              <div className="flex justify-center rounded-2xl border border-border bg-surface py-6 shadow-sm">
                <Spinner className="h-6 w-6" />
              </div>
            </ConnectedPlatformSection>
          ) : brandsQuery.isError ? (
            <ConnectedPlatformSection title="Brands">
              <div className="rounded-2xl border border-border bg-surface px-4 py-5 text-center text-foreground-muted shadow-sm">
                <p className="text-body-md">Could not load brands.</p>
                <Button type="button" className="mt-3" onClick={() => void brandsQuery.refetch()}>
                  Retry
                </Button>
              </div>
            </ConnectedPlatformSection>
          ) : brands.length === 0 ? (
            <ConnectedPlatformSection title="Brands">
              <EmptyAccountState
                title="No brands yet."
                detail="Create a brand on the Brands page, then it will show up here."
              />
            </ConnectedPlatformSection>
          ) : (
            <ConnectedPlatformSection title="Brands">
              <div className="space-y-2">
                {brands.map((brand) => (
                  <BrandAccountListRow key={brand.id} brand={brand} />
                ))}
              </div>
            </ConnectedPlatformSection>
          )
        ) : null}

        {!isDemo ? <ConnectAccountsCta hasAccounts={hasAnyAccount} /> : null}

        <AnalysisActionBar />
      </main>
    </div>
  );
}
